import { z } from 'zod';
const canonicalUtcTimestampSchema = z.string().refine((value) => {
    const timestamp = new Date(value);
    return !Number.isNaN(timestamp.valueOf()) && timestamp.toISOString() === value;
}, 'Expected a canonical RFC 3339 UTC instant');
const basisPointsSchema = z.number().int().min(0).max(10000);
const signedBasisPointsSchema = z.number().int().min(-10000).max(10000);
const finiteLineSchema = z.number().finite();
export const publicUnavailableReasonSchema = z.enum([
    'not_available',
    'phase_not_supported',
    'out_of_date',
    'temporarily_unavailable',
]);
export const comparisonValuesSchema = z.object({
    polymarketImpliedProbabilityBps: basisPointsSchema,
    traditionalImpliedProbabilityBps: basisPointsSchema,
    signedDifferenceBps: signedBasisPointsSchema,
}).strict().superRefine((values, context) => {
    if (values.signedDifferenceBps
        !== values.polymarketImpliedProbabilityBps - values.traditionalImpliedProbabilityBps) {
        context.addIssue({
            code: 'custom',
            path: ['signedDifferenceBps'],
            message: 'Expected Polymarket minus traditional implied probability',
        });
    }
});
const matchWinnerOutcomeSchema = z.object({
    side: z.enum(['home', 'draw', 'away']),
    line: z.null(),
    values: comparisonValuesSchema,
}).strict();
const spreadOutcomeSchema = z.object({
    side: z.enum(['home', 'away']),
    line: finiteLineSchema,
    values: comparisonValuesSchema,
}).strict();
const totalGoalsOutcomeSchema = z.object({
    side: z.enum(['over', 'under']),
    line: finiteLineSchema.positive(),
    values: comparisonValuesSchema,
}).strict();
export const matchWinnerComparisonSchema = z.object({
    family: z.literal('match_winner'),
    outcomes: z.tuple([
        matchWinnerOutcomeSchema.extend({ side: z.literal('home') }),
        matchWinnerOutcomeSchema.extend({ side: z.literal('draw') }),
        matchWinnerOutcomeSchema.extend({ side: z.literal('away') }),
    ]),
}).strict();
export const spreadComparisonSchema = z.object({
    family: z.literal('spread'),
    outcomes: z.tuple([
        spreadOutcomeSchema.extend({ side: z.literal('home') }),
        spreadOutcomeSchema.extend({ side: z.literal('away') }),
    ]),
}).strict().superRefine((market, context) => {
    if (market.outcomes[1].line !== -market.outcomes[0].line) {
        context.addIssue({
            code: 'custom',
            path: ['outcomes', 1, 'line'],
            message: 'Expected the away line to be the inverse of the home line',
        });
    }
});
export const totalGoalsComparisonSchema = z.object({
    family: z.literal('total_goals'),
    outcomes: z.tuple([
        totalGoalsOutcomeSchema.extend({ side: z.literal('over') }),
        totalGoalsOutcomeSchema.extend({ side: z.literal('under') }),
    ]),
}).strict().superRefine((market, context) => {
    if (market.outcomes[1].line !== market.outcomes[0].line) {
        context.addIssue({
            code: 'custom',
            path: ['outcomes', 1, 'line'],
            message: 'Expected Over and Under to use the same line',
        });
    }
});
export const marketComparisonSchema = z.union([
    matchWinnerComparisonSchema,
    spreadComparisonSchema,
    totalGoalsComparisonSchema,
]);
const provenanceSchema = z.object({
    polymarket: z.object({
        provider: z.literal('polymarket'),
        observedAt: canonicalUtcTimestampSchema,
    }).strict(),
    traditionalOdds: z.object({
        provider: z.literal('api_football'),
        observedAt: canonicalUtcTimestampSchema,
        observationCount: z.number().int().positive(),
    }).strict(),
    projectedAt: canonicalUtcTimestampSchema,
    freshness: z.literal('fresh'),
}).strict();
const availableCurrentComparisonSchema = z.object({
    kind: z.literal('current_comparison'),
    status: z.literal('available'),
    oddsPhase: z.literal('pre_match'),
    markets: z.array(marketComparisonSchema).nonempty(),
    provenance: provenanceSchema,
}).strict();
const unavailableCurrentComparisonSchema = z.object({
    kind: z.literal('current_comparison'),
    status: z.literal('unavailable'),
    reason: publicUnavailableReasonSchema,
}).strict();
export const currentComparisonSchema = z.discriminatedUnion('status', [
    availableCurrentComparisonSchema,
    unavailableCurrentComparisonSchema,
]);
export const historicalPreMatchReferenceSchema = z.object({
    kind: z.literal('historical_pre_match_reference'),
    status: z.literal('available'),
    oddsPhase: z.literal('pre_match'),
    snapshotAt: canonicalUtcTimestampSchema,
    markets: z.array(marketComparisonSchema).nonempty(),
    provenance: provenanceSchema,
}).strict();
export const marketPresentationSchema = z.object({
    fixtureId: z.string().trim().min(1),
    currentComparison: currentComparisonSchema,
    historicalPreMatchReference: historicalPreMatchReferenceSchema.nullable(),
}).strict().nullable();
